import { commonEn } from './common'
import { domainsEn } from './domains'
import { mergeDictionaries, type Dictionary } from './types'

const shellNavEn: Dictionary = {
  shell: {
    searchPlaceholder: 'Quick search…',
    notifications: 'Notifications',
    pendingCount: '{count} unread',
    noPending: 'No unread notifications',
    markAllRead: 'Mark all as read',
    emptyNotifications: 'No notifications yet',
    justNow: 'Just now',
    minutesAgo: '{count}m ago',
    hoursAgo: '{count}h ago',
    daysAgo: '{count}d ago',
    notifCat: {
      orders: 'Orders',
      payments: 'Payments',
      tickets: 'Support',
      messages: 'Messages',
      livechat: 'Live chat',
      adverts: 'Listings',
      wallet: 'Wallet',
    },
    lightTheme: 'Light theme',
    darkTheme: 'Dark theme',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    sidebar: 'Sidebar',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    collapse: 'Collapse',
    logout: 'Log out',
    logoutFull: 'Log out',
    logoutDone: 'Signed out',
    menuLoading: 'Loading menu…',
    planModules: 'Plan modules',
    footerTagline: 'CodexFix · Modern Ecommerce Admin',
    footerBuiltWith: 'This panel is powered by the CodexFix platform.',
    sidebarFooter: 'Powered by CodexFix',
    platformBanner:
      'Platform support session — you entered the customer admin as an administrator.',
    closeSession: 'End session',
  },
  nav: {
    section: {
      sales: 'Sales Management',
      products: 'Product Management',
      members: 'Members & Support',
      content: 'Content Management',
    },
    home: 'Home',
    reports: 'Reports',
    salesReports: 'Sales Reports',
    stockReports: 'Stock Reports',
    memberReports: 'Member Reports',
    analytics: 'Analytics',
    ordersPayments: 'Orders & Payments',
    orders: 'Orders',
    orderTickets: 'Customer Requests',
    frauds: 'Suspicious Activity',
    transactions: 'Transactions',
    paymentNotifications: 'Payment Notifications',
    balanceRequests: 'Balance Withdrawals',
    invoices: 'Invoices',
    advertsMgmt: 'Listings',
    adverts: 'Listings',
    advertDopings: 'Listing boosts',
    sellerRequests: 'Seller account requests',
    sellerWarnings: 'Seller warnings',
    phoneRequests: 'Call requests',
    messageTemplates: 'Message templates',
    profileAddons: 'Profile add-ons',
    advertCategories: 'Listing Categories',
    advertComments: 'Seller Reviews',
    advertCommissions: 'Marketplace commissions',
    marketplaceHub: 'Marketplace integration',
    advertRules: 'Listing Rules',
    categoryMgmt: 'Categories',
    categories: 'Categories',
    brands: 'Brands',
    categoryTypes: 'Category Types',
    platforms: 'Category Platforms',
    productMgmt: 'Products',
    products: 'Products',
    productTypes: 'Product Types',
    stockMgmt: 'Inventory',
    stocks: 'Stock',
    virtualStocks: 'Virtual Stock',
    suppliers: 'Suppliers',
    supplierTransactions: 'Supplier Transactions',
    marketplaceMgmt: 'Stores',
    marketplaces: 'Stores',
    marketplacePrices: 'Store Product Prices',
    marketplaceApplications: 'Store Applications',
    customerMgmt: 'Customers',
    customers: 'Customers',
    customerTypes: 'Customer types',
    customerRestrictions: 'Customer restrictions',
    customerApi: 'Customer API',
    purchases: 'Purchase list',
    apiOrders: 'API orders',
    ticketTypes: 'Ticket types',
    purchaseReports: 'Purchase reports',
    supplierBanks: 'Supplier bank accounts',
    marketplaceCategories: 'Store categories',
    customerTransactions: 'Customer Transactions',
    customerNotifications: 'Customer Notifications',
    staffMgmt: 'Staff',
    staff: 'Staff',
    permissions: 'Permissions',
    supportMgmt: 'Support',
    inbox: 'Inbox',
    whatsapp: 'WhatsApp Chat',
    livechat: 'Live Chat',
    tickets: 'Help Desk',
    couponMgmt: 'Coupons',
    coupons: 'Coupons',
    wheel: 'Lucky Wheel',
    wheelMgmt: 'Wheel Management',
    points: 'Points History',
    themeMgmt: 'Themes',
    themes: 'Design Settings',
    pageTemplates: 'Pages',
    siteContents: 'Site Contents',
    widgets: 'Homepage Widgets',
    headerMenu: 'Header Menu',
    footerMenu: 'Footer Menu',
    legalPages: 'Legal Pages',
    faq: 'FAQ',
    customContents: 'Custom Content',
    kvkk: 'Privacy / Cookies',
    seo: 'SEO & Analytics',
    blogNews: 'Blog & News',
    blogs: 'Blogs',
    blogCategories: 'Blog Categories',
    siteSettings: 'Site Settings',
    planModules: 'Plan & Modules',
    generalSettings: 'General Settings',
    security: 'Security & Access',
    messaging: 'Messaging',
    emailTemplates: 'Email Templates',
    panelSettings: 'Panel Settings',
    integrations: 'Integrations',
    payments: 'Payment Processors',
    marketplaceIntegrations: 'Marketplace integrations',
    cargo: 'Shipping Carriers',
    shippingMethods: 'Shipping Methods',
    eInvoice: 'e-Invoice',
    sms: 'SMS Provider',
    partnerApi: 'Partner API',
    topup: 'TopUp Bots',
    aiSettings: 'AI Settings',
  },
  crumbs: {
    dashboard: 'Dashboard',
    products: 'Products',
    orders: 'Orders',
    categories: 'Categories',
    'category-types': 'Category types',
    brands: 'Brands',
    'product-types': 'Product types',
    platforms: 'Category platforms',
    customers: 'Customers',
    coupons: 'Coupons',
    settings: 'General settings',
    'plan-modules': 'Plan & Modules',
    new: 'New',
    login: 'Sign in',
    detail: 'Detail',
    stocks: 'Stock',
    'virtual-stocks': 'Virtual stock',
    marketplaces: 'Stores',
    'marketplace-prices': 'Store prices',
    blogs: 'Blogs',
    themes: 'Themes',
    widgets: 'Widgets',
    'site-contents': 'Site contents',
    'panel-settings': 'Panel settings',
    security: 'Security',
    livechat: 'Live chat',
    tickets: 'Tickets',
    inbox: 'Inbox',
    reports: 'Reports',
    analytics: 'Analytics',
    adverts: 'Listings',
    staff: 'Staff',
    permissions: 'Permissions',
    expenses: 'Expenses',
    wheel: 'Lucky wheel',
    points: 'Points',
    topup: 'TopUp',
    sms: 'SMS',
    cargo: 'Shipping',
    payments: 'Payments',
    invoices: 'Invoices',
    fraud: 'Fraud',
    suppliers: 'Suppliers',
  },
  command: {
    title: 'Command palette',
    placeholder: 'Search pages, categories, products, orders or customers…',
    empty: 'No results found.',
    hint: '↑↓ navigate · Enter select ·',
    quickJump: 'Quick jump',
    menu: 'Menu',
    results: 'Results',
    searching: 'Searching…',
    overview: 'Overview',
    blog: 'Blog',
  },
}

export const en: Dictionary = mergeDictionaries(commonEn, shellNavEn, domainsEn)
